import { FormattedMessage } from 'react-intl';
import { Stack, Typography } from '@mui/material';
import { DATE_FORMAT, FORM_ACTION } from '@ui-framework/index';
import dayjs from 'dayjs';
import { IconLibrary, PermissionButton } from '@product-framework/index';
import DefaultSchedule from '../../utils/DefaultSchedule';

function DeleteMessage({ dated }) {
  return (
    <Typography>
      <FormattedMessage
        id="DO_YOU_WANT_TO_DELETE_THE_DEFAULT_SCHEDULE_WITH_EFFECTIVE_DATE"
        defaultMessage="Do you want to delete the default schedule with effective date"
      />{' '}
      <Typography
        color="primary"
        sx={{ fontWeight: 500 }}
        variant="body"
        component="span">
        {dated}
      </Typography>
      ?
    </Typography>
  );
}

function buttonActions(original, props, accessorFunctionsAdditional) {
  const {
    deleteActionDialog,
    refreshListView,
    params,
    formatMessage,
    locationId = null,
    navigate,
    serviceProviderId,
    mode,
  } = accessorFunctionsAdditional;

  const locationUrl = locationId ? `/locations/${locationId}` : '';

  return (
    <Stack direction="row" spacing={2}>
      <PermissionButton
        permissionName="CAN_VIEW_SP_AVAILABILITY_DEFAULT_SCHEDULE"
        disabled={mode === FORM_ACTION.EDIT}
        size="small"
        variant="contained"
        startIcon={<IconLibrary.View />}
        onClick={() => {
          navigate(
            `/serviceproviders/edit/${serviceProviderId}${locationUrl}/availability/default-schedules/${original?.id}/view`,
          );
        }}>
        <FormattedMessage id="VIEW" defaultMessage="View" />
      </PermissionButton>
      <PermissionButton
        permissionName="CAN_UPDATE_SP_AVAILABILITY_DEFAULT_SCHEDULE"
        disabled={mode === FORM_ACTION.EDIT}
        size="small"
        variant="outlined"
        startIcon={<IconLibrary.Edit />}
        onClick={() => {
          navigate(
            `/serviceproviders/edit/${serviceProviderId}${locationUrl}/availability/default-schedules/${original?.id}/edit`,
          );
        }}>
        <FormattedMessage id="EDIT" defaultMessage="Edit" />
      </PermissionButton>
      <PermissionButton
        permissionName="CAN_DELETE_SP_AVAILABILITY_DEFAULT_SCHEDULE"
        disabled={mode === FORM_ACTION.EDIT}
        size="small"
        variant="outlined"
        color="error"
        startIcon={<IconLibrary.Delete />}
        onClick={() => {
          deleteActionDialog.init({
            message: (
              <DeleteMessage dated={original?.effectiveStartDate || ''} />
            ),
            buttons: [
              {
                label: (
                  <FormattedMessage
                    id="DELETE_SCHEDULE"
                    defaultMessage="Delete Schedule"
                  />
                ),
                onClick: async () => {
                  deleteActionDialog?.updateMessage(
                    <Typography>
                      <FormattedMessage
                        id="DELETING_SCHEDULE"
                        defaultMessage="Deleting Schedule"
                      />
                      ...
                    </Typography>,
                  );
                  const response = await DefaultSchedule.delete({
                    params,
                    defaultScheduleId: original?.id,
                    locationId,
                  });
                  if (response) {
                    params.snackbar.showMessage(
                      formatMessage({
                        id: 'SCHEDULE_WAS_DELETED_SUCCESSFULLY',
                        defaultMessage: 'Schedule was deleted successfully',
                      }),
                    );
                    refreshListView();
                    deleteActionDialog.close();
                  } else {
                    deleteActionDialog?.updateMessage(
                      <DeleteMessage
                        dated={original?.effectiveStartDate || ''}
                      />,
                    );
                  }
                },
                variant: 'contained',
                color: 'error',
                Icon: IconLibrary.Delete,
              },
              {
                label: 'Close',
                onClick: () => {
                  refreshListView();
                  deleteActionDialog.close();
                },
                Icon: IconLibrary.Cancel,
                variant: 'outlined',
              },
            ],
            width: 450,
            Icon: IconLibrary.DefaultSchedule,
            title: (
              <FormattedMessage
                id="DELETE_SCHEDULE"
                defaultMessage="Delete Schedule"
              />
            ),
          });
          deleteActionDialog.open();
        }}>
        <FormattedMessage id="DELETE" defaultMessage="Delete" />
      </PermissionButton>
    </Stack>
  );
}

function effectiveStartDate(original) {
  return original?.effectiveStartDate
    ? dayjs(original?.effectiveStartDate).format(DATE_FORMAT.MEDIUM_DATE)
    : original?.effectiveStartDate;
}

function effectiveEndDate(original) {
  return original?.effectiveEndDate
    ? dayjs(original?.effectiveEndDate).format(DATE_FORMAT.MEDIUM_DATE)
    : original?.effectiveEndDate;
}

export { effectiveStartDate, effectiveEndDate, buttonActions };
